/**
 * axios setup with automatic refresh-token handling on 401
 */

import axios from "axios";
import { environment } from "environments/environment";

const axiosServices = axios.create({
  validateStatus: (status) => {
    return true; // always success-branch; we still handle 401 below
  },
});

// ---- Refresh token state ----
let isRefreshing = false;
let pendingQueue = [];

const processQueue = (error, token = null) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
};

const clearSession = () => {
  localStorage.removeItem("serviceToken");
  localStorage.removeItem("refreshToken");
  delete axiosServices.defaults.headers.common.Authorization;
};

// Handles a 401 by calling /Auth/refresh-token and retrying the original request.
const handle401 = (originalRequest) => {
  const requestUrl = originalRequest?.url || "";
  const isRefreshCall = requestUrl.toLowerCase().includes("/auth/refresh-token");

  if (originalRequest?._retry || isRefreshCall) {
    return Promise.reject(new Error("401 after retry/refresh"));
  }

  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    return Promise.reject(new Error("No refresh token"));
  }

  // If a refresh is already in flight, queue this request.
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      pendingQueue.push({ resolve, reject });
    }).then((newToken) => {
      originalRequest._retry = true;
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axiosServices(originalRequest);
    });
  }

  originalRequest._retry = true;
  isRefreshing = true;

  const currentAccessToken = localStorage.getItem("serviceToken");

  // Use raw axios to bypass our interceptor loop.
  return axios
    .post(
      `${environment.apiUrl}/Auth/refresh-token`,
      { refreshToken },
      {
        headers: currentAccessToken ? { Authorization: `Bearer ${currentAccessToken}` } : undefined,
      },
    )
    .then((refreshResp) => {
      const payload = refreshResp.data?.data || refreshResp.data || {};
      const newAccessToken = payload.accessToken;
      const newRefreshToken = payload.refreshToken;

      if (!newAccessToken) {
        throw new Error("Refresh token request failed");
      }

      localStorage.setItem("serviceToken", newAccessToken);
      if (newRefreshToken) {
        localStorage.setItem("refreshToken", newRefreshToken);
      }
      axiosServices.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

      processQueue(null, newAccessToken);

      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return axiosServices(originalRequest);
    })
    .catch((err) => {
      processQueue(err, null);
      clearSession();
      if (typeof window !== "undefined") {
        window.location.href = "/login";
      }
      return Promise.reject(err);
    })
    .finally(() => {
      isRefreshing = false;
    });
};

// interceptor for http
axiosServices.interceptors.response.use(
  (response) => {
    if (response.status === 401) {
      return handle401(response.config || {});
    }
    return response;
  },
  (error) => {
    // Treat true 401 OR network/CORS-blocked errors (response is undefined because the
    // backend returns 401 without CORS headers) as auth failures and try to refresh.
    const isCorsBlocked401 =
      !error?.response &&
      (error?.code === "ERR_NETWORK" || /Network Error/i.test(error?.message || ""));

    if (error?.response?.status === 401 || isCorsBlocked401) {
      return handle401(error.config || {});
    }
    return Promise.reject((error.response && error.response.data) || "Wrong Services");
  },
);

export default axiosServices;
